/**
 * Client-side month selection for Avg Revenue.
 * Default = last up to 3 full months (MTD/partial excluded until the user adds them).
 */

import { format, parseISO } from "date-fns";

import { fmtMoneyShort } from "@/utils/optimisticAnalysisRecompute";
import type { AnalysisOutput, RevenueBridgeMonth, RevenueLine } from "@/types/underwriting";

export type AvgRevenueMonth = {
  monthKey: string;
  month_abbrev: string;
  amount: number;
  amount_display: string;
  isFull: boolean;
};

export function monthKeyToAbbrev(key: string): string {
  try {
    return format(parseISO(`${key}-01`), "MMM");
  } catch {
    return key;
  }
}

function takeQueuedByAbbrev(
  byAbbrev: Map<string, RevenueLine[]>,
  abbrev: string
): RevenueLine | undefined {
  const queue = byAbbrev.get(abbrev);
  if (!queue?.length) return undefined;
  return queue.shift();
}

/** Build one entry per observed month, preferring revenue_bridge keys for identity. */
export function buildAvgRevenueMonths(
  revenueLines: RevenueLine[],
  revenueBridge: RevenueBridgeMonth[] | undefined,
  fallbackMonthKeys: string[]
): AvgRevenueMonth[] {
  const byAbbrev = new Map<string, RevenueLine[]>();
  for (const rl of revenueLines) {
    const list = byAbbrev.get(rl.month_abbrev) ?? [];
    list.push(rl);
    byAbbrev.set(rl.month_abbrev, list);
  }

  if (revenueBridge?.length) {
    return revenueBridge.map((b) => {
      const abbrev = monthKeyToAbbrev(b.month);
      const rl = takeQueuedByAbbrev(byAbbrev, abbrev);
      const amount = rl?.amount ?? b.true_revenue;
      return {
        monthKey: b.month,
        month_abbrev: rl?.month_abbrev ?? abbrev,
        amount,
        amount_display: rl?.amount_display ?? fmtMoneyShort(amount),
        isFull: b.is_full_month,
      };
    });
  }

  const usedKeys = new Set<string>();
  return revenueLines.map((rl, index) => {
    const candidates = fallbackMonthKeys.filter(
      (k) => monthKeyToAbbrev(k) === rl.month_abbrev && !usedKeys.has(k)
    );
    const monthKey = candidates[0] ?? fallbackMonthKeys[index] ?? rl.month_abbrev;
    usedKeys.add(monthKey);
    return {
      monthKey,
      month_abbrev: rl.month_abbrev,
      amount: rl.amount,
      amount_display: rl.amount_display,
      isFull: true,
    };
  });
}

/** Last up to `window` full-month keys; falls back to excluding the current calendar month. */
export function defaultAvgMonthKeys(
  months: AvgRevenueMonth[],
  window = 3,
  asOf = new Date()
): string[] {
  const full = months.filter((m) => m.isFull).map((m) => m.monthKey);
  if (full.length > 0) return full.slice(-window);

  const currentYm = format(asOf, "yyyy-MM");
  const keys = months.map((m) => m.monthKey);
  const completed = keys.filter((k) => k < currentYm);
  const pool = completed.length > 0 ? completed : keys;
  return pool.slice(-window);
}

export function averageForMonthKeys(months: AvgRevenueMonth[], selectedKeys: string[]): number {
  if (selectedKeys.length === 0) return 0;
  const byKey = new Map(months.map((m) => [m.monthKey, m.amount]));
  let sum = 0;
  let count = 0;
  for (const key of selectedKeys) {
    const amount = byKey.get(key);
    if (amount === undefined) continue;
    sum += amount;
    count += 1;
  }
  return count === 0 ? 0 : sum / count;
}

/** Keep prior selection when months still exist; otherwise reset to the default trailing full months. */
export function reconcileAvgMonthSelection(
  months: AvgRevenueMonth[],
  previous: string[] | null
): string[] {
  if (previous == null) return defaultAvgMonthKeys(months);
  const available = new Set(months.map((m) => m.monthKey));
  const kept = previous.filter((k) => available.has(k));
  return kept.length > 0 ? kept : defaultAvgMonthKeys(months);
}

export function formatAvgRevenueDisplay(average: number): string {
  return fmtMoneyShort(average);
}

/** Stable fingerprint so selection resets when the analysis month set changes. */
export function avgMonthsFingerprint(
  analysis: Pick<AnalysisOutput, "revenue_bridge" | "revenue_lines">
): string {
  if (analysis.revenue_bridge?.length) {
    return analysis.revenue_bridge
      .map((b) => `${b.month}:${b.is_full_month ? "F" : "P"}`)
      .join("|");
  }
  return (analysis.revenue_lines ?? []).map((rl) => rl.month_abbrev).join("|");
}
